import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState, ReactNode } from 'react';

import { formatCurrency, formatNumber } from '@/utils';

import {
  CurrencyContext,
  type CurrencyContextType,
  type CurrencyRates,
  type CurrencyFormatOptions,
  type CurrencyRangeFormat,
} from './CurrencyContextTypes';

// Re-export for hook usage
export { CurrencyContext };

const DEFAULT_CURRENCY = 'USD';
const DEFAULT_RATES = { USD: 1 };
const SELECTED_CURRENCY_KEY = 'medipaths_selected_currency_v1';
const RATES_TTL = 1000 * 60 * 60 * 12; // 12 hours
const BASE_CURRENCY = 'USD';

const getDefaultCurrency = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_CURRENCY;
  }
  const stored = window.localStorage.getItem(SELECTED_CURRENCY_KEY);
  return stored ?? DEFAULT_CURRENCY;
};

function normalizeCurrencyCode(code: unknown): string {
  if (!code || typeof code !== 'string') return DEFAULT_CURRENCY;
  return code.trim().toUpperCase();
}

// React Query hook for fetching currency rates
const fetchCurrencyRates = async (): Promise<{ rates: CurrencyRates; lastUpdated: number }> => {
  const response = await fetch('https://open.er-api.com/v6/latest/USD');
  if (!response.ok) {
    throw new Error(`Currency API responded with ${response.status}`);
  }

  const data: unknown = await response.json();

  if (!data || typeof data !== 'object') {
    throw new Error('Received invalid response data');
  }

  const dataObj = data as Record<string, unknown>;
  const apiRates = dataObj.rates;

  if (!apiRates || typeof apiRates !== 'object') {
    throw new Error('Received invalid exchange rate data');
  }

  const updatedAt =
    typeof dataObj.time_last_update_unix === 'number'
      ? dataObj.time_last_update_unix * 1000
      : Date.now();

  return {
    rates: apiRates as CurrencyRates,
    lastUpdated: updatedAt,
  };
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export function CurrencyProvider({ children }: CurrencyProviderProps) {
  const [selectedCurrency, setSelectedCurrencyState] = useState(getDefaultCurrency);

  // Use React Query for currency rates
  const {
    data: ratesData,
    isLoading: loading,
    error: ratesError,
  } = useQuery({
    queryKey: ['currency', 'rates'],
    queryFn: fetchCurrencyRates,
    staleTime: RATES_TTL,
    gcTime: RATES_TTL * 2, // Keep in cache for 24 hours
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  const rates: CurrencyRates = ratesData?.rates ?? DEFAULT_RATES;
  const lastUpdated = ratesData?.lastUpdated ?? null;
  const error: string | null = ratesError?.message ?? null;

  const setSelectedCurrency = useCallback((currencyCode: string) => {
    if (typeof window === 'undefined') return;
    const normalized = normalizeCurrencyCode(currencyCode);
    setSelectedCurrencyState(normalized);
    window.localStorage.setItem(SELECTED_CURRENCY_KEY, normalized);
  }, []);

  const availableCurrencies = useMemo(() => {
    return Object.keys(rates)
      .filter(code => typeof code === 'string' && code.length === 3)
      .sort();
  }, [rates]);

  const getRate = useCallback(
    (currencyCode: string): number | null => {
      const normalized = normalizeCurrencyCode(currencyCode);
      if (typeof normalized === 'string' && normalized in rates) {
        const rate = rates[normalized as keyof typeof rates];
        return typeof rate === 'number' ? rate : null;
      }
      return null;
    },
    [rates]
  );

  const convertAmount = useCallback(
    (
      amountInUsd: number | null | undefined,
      currencyCode: string = selectedCurrency
    ): number | null => {
      if (amountInUsd === null || amountInUsd === undefined || Number.isNaN(Number(amountInUsd))) {
        return null;
      }

      const rate = getRate(currencyCode);
      if (!rate) {
        return null;
      }

      return Number(amountInUsd) * rate;
    },
    [getRate, selectedCurrency]
  );

  const formatAmount = useCallback(
    (
      amountInUsd: number | null | undefined,
      {
        currency = selectedCurrency,
        minimumFractionDigits,
        maximumFractionDigits,
      }: CurrencyFormatOptions = {}
    ): string | null => {
      const converted = convertAmount(amountInUsd, currency);
      if (converted === null) {
        return null;
      }

      const numericMaximum =
        typeof maximumFractionDigits === 'number'
          ? Math.max(0, maximumFractionDigits)
          : converted >= 1000
            ? 0
            : 2;

      const numericMinimum =
        typeof minimumFractionDigits === 'number'
          ? Math.max(0, Math.min(numericMaximum, minimumFractionDigits))
          : Math.min(2, numericMaximum);

      const fallbackLabel = `${currency} ${formatNumber(converted, {
        formatOptions: {
          minimumFractionDigits: numericMinimum,
          maximumFractionDigits: numericMaximum,
        },
        fallback: String(converted),
      })}`;

      return formatCurrency(converted, {
        currency,
        formatOptions: {
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: numericMinimum,
          maximumFractionDigits: numericMaximum,
        },
        fallback: fallbackLabel,
      });
    },
    [convertAmount, selectedCurrency]
  );

  const formatRange = useCallback(
    (
      minUsd: number | null | undefined,
      maxUsd: number | null | undefined,
      options: CurrencyFormatOptions = {}
    ): CurrencyRangeFormat | null => {
      const currency = options.currency ?? selectedCurrency;

      const formattedMin = minUsd != null ? formatAmount(minUsd, { ...options, currency }) : null;
      const formattedMax = maxUsd != null ? formatAmount(maxUsd, { ...options, currency }) : null;

      if (!formattedMin && !formattedMax) {
        return null;
      }

      const label = createRangeLabel(formattedMin, formattedMax);

      return {
        currency,
        min: formattedMin ?? formattedMax,
        max: formattedMax ?? formattedMin,
        label,
      };
    },
    [formatAmount, selectedCurrency]
  );

  // Helper function to create range label
  const createRangeLabel = (min: string | null, max: string | null): string => {
    if (min && max) {
      return `${min} - ${max}`;
    }
    return min ?? max ?? '';
  };

  const value = useMemo<CurrencyContextType>(
    () => ({
      baseCurrency: BASE_CURRENCY,
      selectedCurrency,
      setSelectedCurrency,
      rates,
      lastUpdated,
      loading,
      error,
      availableCurrencies,
      convertAmount,
      formatAmount,
      formatRange,
      getRate,
    }),
    [
      availableCurrencies,
      convertAmount,
      error,
      formatAmount,
      formatRange,
      getRate,
      lastUpdated,
      loading,
      rates,
      selectedCurrency,
      setSelectedCurrency,
    ]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}
